import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Container, Section, Eyebrow } from '@/components/primitives/Container';
import { Button } from '@/components/primitives/Button';
import { cn } from '@/lib/cn';

type Profile = 'docente' | 'directivo' | 'especialista' | 'desarrollador' | 'otro';

const PROFILES: { id: Profile; label: string }[] = [
  { id: 'docente', label: 'Docente' },
  { id: 'directivo', label: 'Directivo / supervisor' },
  { id: 'especialista', label: 'Especialista o equipo técnico ministerial' },
  { id: 'desarrollador', label: 'Desarrollador de IA' },
  { id: 'otro', label: 'Otro' },
];

export function Contacto() {
  const [searchParams] = useSearchParams();
  const initialProfile = (searchParams.get('perfil') as Profile | null) ?? null;

  const [profile, setProfile] = useState<Profile | null>(
    initialProfile && PROFILES.some((p) => p.id === initialProfile) ? initialProfile : null,
  );
  const [submitted, setSubmitted] = useState(false);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <>
      <Section className="pt-12 sm:pt-16">
        <Container width="editorial">
          <Eyebrow>Contacto</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-normal tracking-tight sm:text-5xl">
            ¿Quién sos?
          </h1>
          <p className="mt-6 max-w-prose text-lg leading-relaxed text-ink-muted">
            Tu respuesta orienta el formulario y el equipo que recibe tu mensaje.
          </p>
        </Container>
      </Section>

      <Section className="pt-0">
        <Container width="editorial">
          {!submitted ? (
            <>
              <fieldset>
                <legend className="sr-only">Elegí tu perfil</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {PROFILES.map((p) => (
                    <label
                      key={p.id}
                      className={cn(
                        'focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-2 cursor-pointer rounded-lg border p-4 transition-colors',
                        profile === p.id
                          ? 'border-ink bg-ink text-paper'
                          : 'border-line bg-paper text-ink hover:border-line_strong',
                      )}
                    >
                      <input
                        type="radio"
                        name="profile"
                        value={p.id}
                        checked={profile === p.id}
                        onChange={() => setProfile(p.id)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{p.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {profile && (
                <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-5">
                  <Field label="Nombre" required>
                    <input
                      type="text"
                      name="name"
                      required
                      className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                    />
                  </Field>
                  {(profile === 'directivo' || profile === 'especialista') && (
                    <Field label="Institución / Equipo">
                      <input
                        type="text"
                        name="institution"
                        className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                      />
                    </Field>
                  )}
                  <Field label="¿Qué necesitás?" required>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      className="focus-ring w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                      placeholder="Contanos brevemente. No hace falta dar contexto técnico."
                    />
                  </Field>
                  <label className="flex items-start gap-2 text-xs text-ink-muted">
                    <input type="checkbox" required className="mt-0.5" />
                    <span>
                      Acepto que me contacten por correo. No comparten mis datos con terceros.
                    </span>
                  </label>
                  <div>
                    <Button type="submit">Enviar</Button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <div role="status" className="rounded-lg border border-status-covered/40 bg-status-covered/5 p-6">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-status-covered text-paper">
                  <Check size={14} />
                </span>
                <div>
                  <p className="font-serif text-lg italic text-ink">Recibimos tu mensaje.</p>
                  <p className="mt-2 text-sm text-ink-muted">
                    El equipo que corresponde te va a responder por correo. No mandamos newsletter
                    ni promociones — sólo respuestas a tu consulta.
                  </p>
                </div>
              </div>
            </div>
          )}
        </Container>
      </Section>

      <Section ariaLabelledby="canales-title" className="border-t border-line">
        <Container width="editorial">
          <Eyebrow>Canales adicionales</Eyebrow>
          <h2 id="canales-title" className="mt-3 font-serif text-2xl font-normal tracking-tight">
            Otros caminos.
          </h2>
          <p className="prose-editorial mt-6 text-sm">
            Si preferís correo directo o necesitás contactar al editor responsable, los datos están
            en{' '}
            <a href="/legal#editor" className="focus-ring rounded text-accent hover:underline">
              la página legal
            </a>
            .
          </p>
        </Container>
      </Section>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink">
        {label}
        {required && <span className="ml-1 text-status-progress">*</span>}
      </span>
      {children}
    </label>
  );
}
